import { Form } from "../../../../components/common/Forms";
import tuitionStepFields, { fieldApiIdMap, commentTypeMap } from "../../../../config/tuitionFormConfig";
import { FC, useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { RootState } from "../../../../store/store";
import Loader from "../../../../components/common/Loader";
import { downloadPrintApplication, downloadPrintFaxCover, fetchApplicationDetails } from "../../../../store/slices/Tuition/tuitionViewMyStatusSlice";
import { formatDateYMDToMDY, formatTimestamp } from "../../../../utils/date";
import Notifications from "../../../../components/common/Notifications";
import UploadErrorBanner from "../../../CitizenshipReimbursement/UploadDocument/ViewDetails/UploadErrorBanner";

// ==================== UTILITY FUNCTIONS ====================



interface TuitionApplicationTabProps {
  formData?: any;
}

// ==================== HELPER TYPES ====================

interface StepConfig {
  id: string;
  title: string;
  subtitle?: string;
  fields?: any[];
  renderContent?: JSX.Element | null;
  updatedBy?: string;
  updatedDate?: string;
}

interface CeuConditionSection {
  title: string;
  subtitle: string;
  fieldGroupType: string;
}

// ==================== REUSABLE COMPONENT: FormSection ====================

/**
 * Renders a form section with optional header and metadata
 * Eliminates repetition of h4 + subtitle + Form + UpdatedBy/Date pattern
 */
interface FormSectionProps {
  title?: string;
  subtitle?: string;
  fields: any[];
  initialValues: any;
  showMetadata?: boolean;
  updatedBy?: string;
  updatedDate?: string;
}

const FormSection: FC<FormSectionProps> = ({
  title,
  subtitle,
  fields,
  initialValues,
  showMetadata = false,
  updatedBy,
  updatedDate,
}) => (
  <div>
    {title && <h4 className="h4 margin-bottom-2u">{title}</h4>}
    {subtitle && <p className="p3 -book mb-6!">{subtitle}</p>}
    {showMetadata && (updatedBy || updatedDate) && (
      <div className="flex gap-70 mb-3.5!">
        {updatedBy && <p className="p3 -book">Updated by:{updatedBy}</p>}
        {updatedDate && <p className="p3 -book">Updated date :{updatedDate}</p>}
      </div>
    )}
    <Form fields={fields} initialValues={initialValues} buttons={[]} isViewDetails={true} />
  </div>
);

// ==================== REUSABLE COMPONENT: MetadataDisplay ====================

/**
 * Renders updated by and date metadata
 */
interface MetadataDisplayProps {
  updatedBy?: string;
  updatedDate?: string;
  showHr?: boolean;
}

const MetadataDisplay: FC<MetadataDisplayProps> = ({
  updatedBy,
  updatedDate,
  showHr = false,
}) => (
  <>
    <div className="flex gap-70 mb-3.5!">
      {updatedBy && <p className="p3 -book">Updated by:{updatedBy}</p>}
      {updatedDate && <p className="p3 -book">Updated date :{updatedDate}</p>}
    </div>
    {showHr && <hr className="-variation-1" />}
  </>
);

// ==================== REUSABLE COMPONENT: CeuConditionalSection ====================

/**
 * Renders a conditional section that only displays for CEU reimbursement type
 */
interface CeuConditionalSectionProps {
  course: any;
  reimbursementTypeCode: string | number;
  displayReimbursementType: string;
  section: CeuConditionSection;
  fields: any[];
  getDisplayValue: (fieldName: string, value: any) => string;
}

const CeuConditionalSection: FC<CeuConditionalSectionProps> = ({
  course,
  reimbursementTypeCode,
  displayReimbursementType,
  section,
  fields,
  getDisplayValue,
}) => {
  const isCeu =
    String(reimbursementTypeCode) === "1" || displayReimbursementType === "CEU";

  if (!isCeu) return null;

  return (
    <div className="mt-6">
      <h4 className="h4 margin-bottom-2u">{section.title}</h4>
      <p className="p3 -book mb-6!">{section.subtitle}</p>
      <Form
        fields={fields}
        initialValues={{
          ...course,
          originZipCode: course?.originZip || "",
          destinationZipCode:course?.destinationZip || "",
          originState: getDisplayValue("originState", course?.originState),
          destinationState: getDisplayValue("destinationState", course?.destinationState),
        }}
        buttons={[]}
        isViewDetails={true}
      />
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const TuitionApplicationTab: FC<TuitionApplicationTabProps> = ({ formData }) => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const { items } = useSelector((state: RootState) => state.tuitionViewMyStatus);
  const { dropdownOptions } = useSelector((state: RootState) => state.tuitionApply);
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [isApplicationDetailsLoading, setIsApplicationDetailsLoading] = useState(false);
  const [applicationError, setApplicationError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    if (id) {
      const applicationId = id;
      setCurrentRequestId(applicationId);
      setIsApplicationDetailsLoading(true);
      setApplicationError(null);
      dispatch(fetchApplicationDetails(applicationId) as any)
        .then((result: any) => {
          if (result.type.endsWith("/rejected")) {
            setApplicationError(result.payload as string);
          }
        })
        .finally(() => {
          setIsApplicationDetailsLoading(false);
        });
    }
  }, [id, dispatch]);

  const isCurrentRequestError = currentRequestId === id && applicationError && !isApplicationDetailsLoading;
  const applicationData = items && items.length > 0 ? items[0] : null;

  const mergedFormData = useMemo(
    () => applicationData || formData || {},
    [applicationData, formData]
  );

  const commentsArray = applicationData?.comments || applicationData?.employeeApplicationComments || [];

  const mappedComments = Object.entries(commentTypeMap || {}).reduce(
    (acc: Record<string, any>, [fieldName, commentType]) => {
      const foundValue = (commentsArray || []).find(
        (c: any) => c && String(c.commentDateType) === String(commentType)
      );
      if (foundValue?.comments !== undefined) acc[fieldName] = foundValue.comments;
      return acc;
    },
    {}
  );
 
  const displayValues = useMemo(() => {

    if (!applicationData) return {};
    const raw = { ...mergedFormData, ...mappedComments };
    const dateFields = [
      'empHireDate', 'empRehireDate', 'termEnd', 'termBegin'
    ];
    const formatted: Record<string, any> = { ...raw };
    dateFields.forEach((field) => {
      if (typeof raw[field] === 'string') {
        formatted[field] = formatDateYMDToMDY (raw[field]);
      } else if (raw[field] && typeof raw[field] === 'object') {
        // For nested objects like schoolInfo
        Object.keys(raw[field]).forEach((subField) => {
          if (/date/i.test(subField) && typeof raw[field][subField] === 'string') {
            formatted[field][subField] = formatDateYMDToMDY(raw[field][subField]);
          }
        });
      }
    });
    return formatted;
  }, [applicationData, mergedFormData, mappedComments]);

  const participantConditionValues = useMemo(() => {
    const res: Record<string, any> = {};
    const selections = mergedFormData?.checkboxSelections;
    if (Array.isArray(selections) && selections.length > 0) {
      selections.forEach((sel: any) => {
        const checkboxId = sel.checkboxIdentifyId;
        const mappingKey = Object.keys(fieldApiIdMap).find(
          (k) => fieldApiIdMap[k] === checkboxId
        );
        if (mappingKey) {
          res[mappingKey] = sel.checkboxValue;
          res[String(checkboxId)] = sel.checkboxValue;
        } else {
          res[String(checkboxId)] = sel.checkboxValue;
        }
      });
    }
    return res;
  }, [mergedFormData?.checkboxSelections]);

  const courses = useMemo(
    () => applicationData?.courses || [],
    [applicationData?.courses]
  );

  // ==================== HELPER FUNCTIONS ====================

  const calculateCourseTotalFees = useCallback((course: any) => {
    const feeFieldNames = [
      "estimatedTuitionFee",
      "estimatedBooksFee",
      "estimatedLabFee",
      "estimatedRegistrationFee",
      "estimatedOtherFee",
      "estimatedAirfareBaggageFee",
      "estimatedParkTollFee",
      "estimatedAutoRentalFee",
      "estimatedPublicTransportFee",
      "estimatedHotelFee",
      "estimatedMilesDollarValue",
    ];

    let totalFees = 0;
    feeFieldNames.forEach((fieldName) => {
      const value = Number(course[fieldName]) || 0;
      totalFees += value;
    });

    return totalFees.toFixed(2);
  }, []);

  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, []);

  const getFieldDropdownOptions = useCallback(
    (field: any): any[] | undefined => {
      if (field.name === "reimbursementType" && dropdownOptions.reimbursement_type) {
        return dropdownOptions.reimbursement_type.map((item: any) => ({
          value: item.value,
          label: item.value,
        }));
      }

      if (
        (field.name === "originState" || field.name === "destinationState") &&
        dropdownOptions.state
      ) {
        return dropdownOptions.state.map((item: any) => ({
          value: item.value,
          label: item.value,
        }));
      }

      return undefined;
    },
    [dropdownOptions]
  );

  const getDisplayValue = useCallback(
    (fieldName: string, codeValue: string | number | undefined): string => {
      if (!codeValue && codeValue !== 0) return "";

      if (fieldName === "reimbursementType" && dropdownOptions.reimbursement_type) {
        const found = dropdownOptions.reimbursement_type.find(
          (item: any) => String(item.code) === String(codeValue)
        );
        return found ? found.value : String(codeValue);
      }

      if (
        (fieldName === "originState" || fieldName === "destinationState") &&
        dropdownOptions.state
      ) {
        const found = dropdownOptions.state.find(
          (item: any) => String(item.code) === String(codeValue)
        );
        return found ? found.value : String(codeValue);
      }

      if (fieldName === "applicationTerm" && dropdownOptions.application_term) {
        const found = dropdownOptions.application_term.find(
          (item: any) => String(item.code) === String(codeValue)
        );
        return found ? found.value : String(codeValue);
      }

      return String(codeValue);
    },
    [dropdownOptions]
  );

     const handlePrintFaxCover = useCallback(async () => {
    if (!id) {
      console.error("No application id provided.");
      return;
    }
    setDownloadError(null);
    setIsPrinting(true);
    const action = await dispatch<any>(
      downloadPrintFaxCover({ applicationId: id})
    );

    setIsPrinting(false);

    if (!downloadPrintFaxCover.fulfilled.match(action)) {
      setDownloadError(String(action.payload || "Failed to download fax cover sheet"));
    }
  }, [id, dispatch]);

    const handlePrintApplication = async () => {
    
    if (!id) {
      console.error("No application id provided.");
      return;
    }
    setDownloadError(null);
    setIsPrinting(true);
    const action = await dispatch<any>(
      downloadPrintApplication({ applicationId: id})
    );

    setIsPrinting(false);

    if (!downloadPrintApplication.fulfilled.match(action)) {
      setDownloadError(String(action.payload || "Failed to download application"));
    }
  };

  const createLockedFields = useCallback(
    (fields: any[]) => {
      return fields.map((field) => {
        const fieldOptions = getFieldDropdownOptions(field);
        return {
          ...field,
          disabled: true,
          helperText: "Read only; this value is assigned automatically by HR",
          required: false,
          type: field.type === "select" ? "text" : field.type,
          placeholder: "",
          isSearchBtnRequired:
            field.name === "alternateNuid" ? false : field.isSearchButtonRequired,
          ...(field.type === "select" && fieldOptions ? { options: fieldOptions } : {}),
        };
      });
    },
    [getFieldDropdownOptions]
  );

  const lockedFieldsPerStep = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(tuitionStepFields).map(([step, fields]) => [
          step,
          createLockedFields(
            (fields as any[]).filter(
              (field) =>
                !(step === "step3" && field.name === "alternateNuid")
            )
          ),
        ])
      ),
    [createLockedFields]
  );

  // Remove generic "Read only" helper text for participant condition radios
  const adjustedLockedFieldsPerStep = useMemo(() => {
    const adjusted = { ...lockedFieldsPerStep };
    if (adjusted?.step7) {
      adjusted.step7 = adjusted.step7.map((f: any) =>
        f.type === "radio" ? { ...f, helperText: "" } : f
      );
    }
    return adjusted;
  }, [lockedFieldsPerStep]);

  // ==================== RENDER FUNCTIONS ====================

  /**
   * Filter fields by group type
   */
  const filterFieldsByGroupType = useCallback(
    (stepKey: string, groupType?: string, includeNames?: string[]): any[] => {
      let fields = adjustedLockedFieldsPerStep[stepKey] || [];
      if (groupType) {
        fields = fields.filter((f: any) => f.fieldGroupType === groupType);
      } else if (includeNames) {
        fields = fields.filter(
          (f: any) => groupType === undefined && includeNames.includes(f.name)
        );
      } else {
        fields = fields.filter((f: any) => !f.fieldGroupType);
      }
      return fields;
    },
    [adjustedLockedFieldsPerStep]
  );

  /**
   * Render employment content sections
   */
  const renderEmploymentContent = useMemo<JSX.Element>(() => {
    const employmentInitialFields = filterFieldsByGroupType("step2", "employmentInitial");
    const atSubmissionFields = employmentInitialFields.filter((f: any) =>
      [
        "shiftStdHours",
        "empHireDate",
        "empRehireDate",
        "empJobStatus",
        "empType",
        "jobTitleCode",
        "jobTitleName",
        "unionName",
      ].includes(f.name)
    );
    const managerFields = filterFieldsByGroupType("step3", "manager");
    const altManagerFields = filterFieldsByGroupType("step3", "managerAlt")
    

    return (
      <div className="space-y-8">
        <div>
          <FormSection fields={employmentInitialFields} initialValues={displayValues} />
        </div>

        <div>
          <FormSection
            title="At submission employment information"
            fields={atSubmissionFields}
            initialValues={
              applicationData?.submittedEmplInfo
                ? {
                    ...applicationData.submittedEmplInfo,
                    empHireDate: formatDateYMDToMDY(applicationData.submittedEmplInfo.empHireDate),
                    empRehireDate: formatDateYMDToMDY(applicationData.submittedEmplInfo.empRehireDate),
                  }
                : displayValues
            }
          />
        </div>

        <div>
          <h4 className="h4 margin-bottom-2u">Manager information</h4>
          <p className="p3 -book mb-6!">
            The following Direct Manager has been identified for you from the HR My Org
            hierarchy. This manager will receive all standard emails and notifications of employee
            education benefit applications submitted here. If known, please provide the manager's
            work phone. If this manager information is incorrect, contact your direct manager or
            supervisor.
          </p>
          <FormSection fields={managerFields} initialValues={displayValues} />
          <p className="p3 -book my-6!">
            If another manager or supervisor should ALSO receive standard emails for approvals and
            notifications of employee education benefit applications submitted here, you may add
            that manager or supervisor here by NUID. If known, please provide the manager's work
            phone. (Note:You may also add or edit that information by accessing the Employee
            information portlet (located in the upper right corner of the My Education Benefit
            Applications page) and update information for your Self-Reported Manager.)
          </p>
          <FormSection fields={altManagerFields} initialValues={displayValues} />
        </div>
      </div>
    );
  }, [filterFieldsByGroupType, displayValues]);

  /**
   * Build dynamic form fields from checkbox selections
   */
  const buildCheckboxDynamicFields = useCallback((checkboxSelections: any[]): any[] => {
    return checkboxSelections.map((item: any) => {
      const checkboxId = item.checkboxIdentifyId;
      const mappingKey = Object.keys(fieldApiIdMap).find(
        (k) => fieldApiIdMap[k] === checkboxId
      );
      const name = mappingKey || String(checkboxId);
      return {
        name,
        label: item.checkboxDescription,
        type: "radio",
        options: ["Yes", "No"],
        required: true,
        disabled: true,
      };
    });
  }, []);

  /**
   * Render participant conditions content
   */
  const renderParticipantContent = useMemo<JSX.Element | null>(() => {
    if (
      Array.isArray(mergedFormData?.checkboxSelections) &&
      mergedFormData.checkboxSelections.length > 0
    ) {
      const dynamicFields = buildCheckboxDynamicFields(
        mergedFormData.checkboxSelections
      );
      return (
        <Form
          fields={dynamicFields}
          initialValues={{ ...displayValues, ...participantConditionValues }}
          buttons={[]}
          isViewDetails={true}
        />
      );
    }
    return null;
  }, [
    mergedFormData?.checkboxSelections,
    displayValues,
    participantConditionValues,
    buildCheckboxDynamicFields,
  ]);

  /**
   * Render comments content
   */
  const renderCommentsContent = useMemo<JSX.Element>(() => {
    const comments = adjustedLockedFieldsPerStep.step6 || [];

    const commentSections = [
      {
        name: "employeeComments",
        title: "Employee comments",
        subtitle: "Briefly explain how this program will help develop your career at Kaiser Permanente.",
      },
      {
        name: "employmentJobComments",
        title: "Employment/Job comments",
        subtitle: "What job at do you plan to advance in with the completion of this program?",
      },
      {
        name: "noEstimatedFees",
        title: "No estimated fees",
        subtitle: "You have not entered an estimated fees or explain why you are unable to estimate the costs.",
      },
    ];

    const hasResponse = (fieldName: string) => {
      const expectedType = (commentTypeMap as any)?.[fieldName];
      if (!expectedType) return false;
      const found = (commentsArray || []).find(
        (c: any) => c && String(c.commentDateType) === String(expectedType)
      );
      return !!found && String(found.comments || "").trim().length > 0;
    };

    return (
      <div>
        {commentSections.map((section) => {
          if (section.name === "noEstimatedFees" && !hasResponse(section.name)) return null;
          const fields = comments.filter((f: any) => f.name === section.name);
          return (
            <div className="mb-6" key={section.name}>
              <FormSection
                title={section.title}
                subtitle={section.subtitle}
                fields={fields.map((f: any) => ({ ...f, label: "Employee comments" }))}
                initialValues={displayValues}
              />
            </div>
          );
        })}
      </div>
    );
  }, [adjustedLockedFieldsPerStep.step6, commentsArray, displayValues]);

  // ==================== BUILD STEPS ARRAY ====================

  const steps = useMemo<StepConfig[]>(() => {
    const baseUpdatedBy = displayValues?.lastUpdatedBy===null?displayValues?.createdBy:displayValues?.lastUpdatedBy;
    const baseUpdatedDate = displayValues?.lastUpdatedTime===null?formatTimestamp(displayValues?.createdTime):formatTimestamp(displayValues?.lastUpdatedTime);

    return [
      {
        id: "applicant-information",
        title: "Applicant information",
        fields: adjustedLockedFieldsPerStep.step1,
        updatedBy: baseUpdatedBy,
        updatedDate: baseUpdatedDate,
      },
      {
        id: "employment-information",
        title: "Employment information",
        subtitle: "Current employment information",
        renderContent: renderEmploymentContent,
        updatedBy: mergedFormData?.txnLastUpdatedBy===null?mergedFormData?.txnCreatedBy:mergedFormData?.txnLastUpdatedBy,
        updatedDate: mergedFormData?.txnLastUpdatedTime===null?formatTimestamp(mergedFormData?.txnCreatedTime):formatTimestamp(mergedFormData?.txnLastUpdatedTime),
      },
      {
        id: "school-information",
        title: "School information",
        fields: adjustedLockedFieldsPerStep.step4,
        updatedBy: applicationData?.schoolInfo?.lastUpdatedBy===null?applicationData?.schoolInfo?.createdBy:applicationData?.schoolInfo?.lastUpdatedBy,
        updatedDate: applicationData?.schoolInfo?.lastUpdatedTime===null?formatTimestamp(applicationData?.schoolInfo?.createdTime):formatTimestamp(applicationData?.schoolInfo?.lastUpdatedTime),
      },
      {
        id: "class-information",
        title: "Class information",
        fields: [],
        updatedBy: courses?.[0]?.lastUpdatedBy===null?courses?.[0]?.createdBy:courses?.[0]?.lastUpdatedBy,
        updatedDate: courses?.[0]?.lastUpdatedTime===null?formatTimestamp(courses?.[0]?.createdTime):formatTimestamp(courses?.[0]?.lastUpdatedTime),
      },
      {
        id: "comments",
        title: "Comments",
        subtitle: "Travel is only reimbursed for continuing education courses and events. Select CEU to enter travel estimates.",
        fields: adjustedLockedFieldsPerStep.step6,
        renderContent: renderCommentsContent,
        updatedBy: commentsArray?.[0]?.lastUpdatedBy ===null?commentsArray?.[0]?.createdBy:commentsArray?.[0]?.lastUpdatedBy,
        updatedDate: commentsArray?.[0]?.lastUpdatedTime===null?formatTimestamp(commentsArray?.[0]?.createdTime):formatTimestamp(commentsArray?.[0]?.lastUpdatedTime),
      },
      {
        id: "participant-conditions",
        title: "Participant conditions",
        fields: adjustedLockedFieldsPerStep.step7,
        renderContent: renderParticipantContent,
        updatedBy: mergedFormData?.checkboxSelections?.[0]?.lastUpdatedBy===null?mergedFormData?.checkboxSelections?.[0]?.createdBy:mergedFormData?.checkboxSelections?.[0]?.lastUpdatedBy,
        updatedDate: mergedFormData?.checkboxSelections?.[0]?.lastUpdatedTime===null?formatTimestamp(mergedFormData?.checkboxSelections?.[0]?.createdTime):formatTimestamp(mergedFormData?.checkboxSelections?.[0]?.lastUpdatedTime),
      },
    ];
  }, [
    displayValues,
    applicationData?.schoolInfo,
    courses,
    commentsArray,
    mergedFormData?.checkboxSelections,
    renderEmploymentContent,
    renderCommentsContent,
    renderParticipantContent,
    adjustedLockedFieldsPerStep,
  ]);

  // ==================== RENDER MAIN COMPONENT ====================

  return (
    <div className="space-y-8">
      {/* Error State */}
      {isCurrentRequestError && (
        <div className="flex items-center justify-center min-h-[300px]">
       <Notifications messageInfo={applicationError} status={false}/>
        </div>
      )}

      {!isCurrentRequestError && (
        <>
          {/* Loading State */}
          {(isApplicationDetailsLoading || !applicationData || isPrinting) && <Loader />}

          {/* Content */}
          {applicationData && (
            <>
              <h3 className="h3 mt-8! mb-4!">Jump to applicant section:</h3>

              {/* Navigation Buttons */}
              <div className="top-0 bg-white z-10 pb-4 mb-4">
                <div className="flex gap-4 flex-wrap">
                  {steps.map((step) => (
                    <button
                      key={step.id}
                      className="button -primary-inverted"
                      onClick={() => scrollToSection(step.id)}
                    >
                      {step.title}
                    </button>
                  ))}
                </div>
              </div>

              <p className="p3 -book mt-4!">
                All fields are optional unless marked as required. Users must fill out one field
                in order to proceed. Once data is inserted within the employeed id and nuid, the
                rest of the fields are auto-inputted.
              </p>

              {/* Sections */}
              {steps.map((step) => (
                <div key={step.id}>
                  <div id={step.id}>
                    {/* Header with metadata (exclude class-information) */}
                    {step.id !== "class-information" && (
                      <>
                        <h3 className="h3 margin-top-2u margin-bottom-2u">{step.title}</h3>
                        {step.subtitle && <h4 className="h4 mb-6!">{step.subtitle}</h4>}
                        <MetadataDisplay
                          updatedBy={step.updatedBy}
                          updatedDate={step.updatedDate}
                        />
                      </>
                    )}

                    {/* Content */}
                    {step.renderContent ?? (
                      <Form
                        fields={step.fields || []}
                        initialValues={
                          step.id === "school-information"
                            ? {
                                ...displayValues?.schoolInfo,
                                termBegin: formatDateYMDToMDY(displayValues?.schoolInfo?.termBegin),
                                termEnd: displayValues?.schoolInfo.termEnd ? formatDateYMDToMDY(displayValues?.schoolInfo?.termEnd) : undefined,
                                applicationTerm: getDisplayValue(
                                  "applicationTerm",
                                  displayValues?.schoolInfo?.applicationTerm,
                                ),
                                degreeCompletionYear: displayValues?.schoolInfo?.degreeCompletionYear === "-100"
                                  ? "Non Degree Program" 
                                  : displayValues?.schoolInfo?.degreeCompletionYear,
                              }
                            : displayValues
                        }
                        buttons={[]}
                        isViewDetails={true}
                      />
                    )}

                    {/* Divider */}
                    {step.id !== "class-information" && step.id !== "participant-conditions" && (
                      <hr className="-variation-1" />
                    )}
                  </div>

                  {/* Dynamic Class Information */}
                  {step.id === "class-information" && courses.length > 0 && (
                    <div id="class-information">
                      <h3 className="h3 margin-top-4u margin-bottom-4u">Class information</h3>                      

                      {/* Course cards */}
                      {courses.map((course: any, index: number) => {
                        const reimbursementTypeCode = course?.reimbursementType;
                        const reimbursementTypeDisplay = getDisplayValue(
                          "reimbursementType",
                          reimbursementTypeCode
                        );

                        const ceuSections: CeuConditionSection[] = [
                          {
                            title: "Travel information - continuing education units only",
                            subtitle: "You need to submit receipts for all travel expenses. Check your policy for travel limits.",
                            fieldGroupType: "travelRelated",
                          },
                          {
                            title: "Total number of miles (round-trip)",
                            subtitle: "Total dollar value will be calculated based on the IRS standard rate",
                            fieldGroupType: "mileageRelated",
                          },
                        ];

                        return (
                          <div key={course.id} className="mb-8">
                            <h4 className="h4 margin-bottom-2u">Class {index + 1}</h4>

                            {/* Per-course metadata */}
                            <div className="flex gap-70 mb-3.5!">
                              <p className="p3 -book">Updated by: {course?.lastUpdatedBy === null ? course?.createdBy : course?.lastUpdatedBy}</p>
                              <p className="p3 -book">Updated date: {course?.lastUpdatedTime === null ? formatTimestamp(course?.createdTime) : formatTimestamp(course?.lastUpdatedTime)}</p>
                            </div>

                            {/* Base course fields */}
                            <Form
                              fields={filterFieldsByGroupType("step5")}
                              initialValues={{
                                ...course,
                                courseStartDate: formatDateYMDToMDY(course?.courseStartDate),
                                courseEndDate: formatDateYMDToMDY(course?.courseEndDate),
                                totalEstimatedCourseFees: calculateCourseTotalFees(course),
                                classSchoolName: course?.schoolName || "",
                                courseProgramType: course?.programType || "",
                                originZipCode: course?.originZip || "",
                                destinationZipCode: course?.destinationZip || "",
                                reimbursementType: reimbursementTypeDisplay,
                                originState: getDisplayValue(
                                  "originState",
                                  course?.originState
                                ),
                                destinationState: getDisplayValue(
                                  "destinationState",
                                  course?.destinationState
                                ),
                              }}
                              buttons={[]}
                              isViewDetails={true}
                            />

                            {/* CEU conditional sections */}
                            {ceuSections.map((section) => (
                              <CeuConditionalSection
                                key={section.fieldGroupType}
                                course={course}
                                reimbursementTypeCode={reimbursementTypeCode}
                                displayReimbursementType={reimbursementTypeDisplay}
                                section={section}
                                fields={filterFieldsByGroupType(
                                  "step5",
                                  section.fieldGroupType
                                )}
                                getDisplayValue={getDisplayValue}
                              />
                            ))}

                            <hr className="-variation-1" />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}

                 {/* Download error banner */}
              {downloadError && (
                <UploadErrorBanner message={"There was an error downloading the document. Please try again later."} header={"Error"} />
              )}

              {/* Print buttons aligned to end */}
              <div className="flex justify-start gap-4 mt-6">
              
                <button className="button -inverted" onClick={handlePrintFaxCover}>
                  Print fax cover sheet
                </button>
                  <button className="button -primary -light" onClick={handlePrintApplication}>
                  Print application
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default TuitionApplicationTab;